import React, { useState } from 'react';
import { FileText, Users, Heart, Calendar, MessageCircle, X, ExternalLink, Download, Palette, Tag } from 'lucide-react';
import Footer from '../components/Footer';

const ParentAdvisory = () => {
  const [selectedDoc, setSelectedDoc] = useState(null);

  const documents = [
    {
      title: 'Welcome Letter 2025',
      description: 'A warm welcome to all families joining us for the 2024-2025 school year. This letter introduces our dedicated team and outlines what families can expect throughout the year.',
      file: '/assets/documents/Parents Committee/Welcome Letter 2025.pdf',
      icon: <Heart className="w-6 h-6" />,
      color: 'from-green-600 to-green-700',
      type: 'pdf',
      category: 'Getting Started'
    },
    {
      title: 'Introduction Letter 2025-2026',
      description: 'Important information about the upcoming school year, including key dates, expectations, and how families can get involved in their child\'s education journey.',
      file: '/assets/documents/Parents Committee/Introduction Letter 25-26.pdf',
      icon: <MessageCircle className="w-6 h-6" />,
      color: 'from-blue-600 to-blue-700',
      type: 'pdf',
      category: 'Getting Started'
    },
    {
      title: 'Parent Leadership Team Letter',
      description: 'Learn about our Parent Leadership Team and opportunities to take on leadership roles within our school community. Discover how you can make a meaningful impact and help shape the future of GoLD.',
      file: '/assets/documents/Parents Committee/Parent Leader Letter.docx.pdf',
      icon: <Users className="w-6 h-6" />,
      color: 'from-indigo-600 to-indigo-700',
      type: 'pdf',
      category: 'Getting Started'
    },
    {
      title: 'Staggered Schedule 2025-2026',
      description: 'Detailed explanation of our staggered entry schedule, designed to help new students transition smoothly into their classroom environment. Includes specific dates and what to expect during the first weeks.',
      file: '/assets/documents/Parents Committee/Staggered Schedule 25-26.pdf',
      icon: <Calendar className="w-6 h-6" />,
      color: 'from-amber-600 to-amber-700',
      type: 'pdf',
      category: 'Schedules & Planning'
    },
    {
      title: 'Parent Handbook / Manual de Padres',
      description: 'Comprehensive bilingual handbook (English & Spanish) covering school policies, procedures, expectations, and resources for families. Essential reading for all parents and guardians.',
      file: '/assets/documents/Parents Committee/_Parent Handbook _ Manual de Padres.pdf',
      icon: <FileText className="w-6 h-6" />,
      color: 'from-purple-600 to-purple-700',
      type: 'pdf',
      category: 'Essential Resources'
    },
    {
      title: 'Art Enrichment Program',
      description: 'Information about our art enrichment program led by Ms. Silibeth Gomez. Learn about the creative experiences and skills your child will develop through our art curriculum.',
      file: '/assets/documents/Parents Committee/Silibeth Gomez.pdf',
      icon: <Palette className="w-6 h-6" />,
      color: 'from-pink-600 to-pink-700',
      type: 'pdf',
      category: 'Enrichment Programs'
    }
  ];

  const categories = [...new Set(documents.map(doc => doc.category))];

  return (
    <div className="min-h-screen bg-white">
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="text-center mb-16 sm:mb-20">
          <span className="text-[#7d5800] font-semibold text-sm uppercase tracking-[0.15em]">Parents</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#123f1e] mt-4 leading-[1.15] tracking-tight">
            Parent Advisory Committee
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mt-6 leading-relaxed font-light max-w-3xl mx-auto">
            Building strong partnerships between families and educators to support student success
          </p>
        </header>

        {/* Mission Statement */}
        <div className="mb-16 bg-[#123f1e] rounded-2xl p-8 sm:p-12 text-white shadow-lg">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-[#febc04]/20 rounded-xl backdrop-blur-sm border border-[#febc04]/30 flex-shrink-0">
              <Users className="w-8 h-8 text-[#febc04]" />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">Our Mission</h2>
              <p className="text-lg sm:text-xl text-white/90 leading-relaxed font-light">
                The Parent Advisory Committee serves as a vital bridge between families and our school community.
                We work collaboratively to enhance the educational experience, support school initiatives, and ensure
                every parent's voice is heard in shaping our children's learning environment.
              </p>
            </div>
          </div>
        </div>

        {/* Parent Involvement Image */}
        <div className="mb-16">
          <img
            src="/assets/images/gallery/District9PreK-25.jpg"
            alt="GoLD Pre-K parents and staff at a school community event"
            className="w-full max-w-5xl mx-auto h-80 object-cover rounded-3xl shadow-xl"
          />
        </div>

        {/* What We Do */}
        <div className="mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-8 tracking-tight">What We Do</h2>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#febc04] transition-all duration-200 shadow-sm hover:shadow-md">
              <div className="w-14 h-14 bg-[#123f1e]/10 rounded-xl flex items-center justify-center mb-4">
                <MessageCircle className="w-7 h-7 text-[#123f1e]" />
              </div>
              <h3 className="text-xl font-bold text-[#123f1e] mb-3 tracking-tight">Advocate & Communicate</h3>
              <p className="text-gray-600 leading-relaxed">
                Serve as a voice for parents, sharing concerns and ideas with school leadership to improve programs and policies.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#febc04] transition-all duration-200 shadow-sm hover:shadow-md">
              <div className="w-14 h-14 bg-[#123f1e]/10 rounded-xl flex items-center justify-center mb-4">
                <Heart className="w-7 h-7 text-[#123f1e]" />
              </div>
              <h3 className="text-xl font-bold text-[#123f1e] mb-3 tracking-tight">Support & Engage</h3>
              <p className="text-gray-600 leading-relaxed">
                Organize family events, volunteer initiatives, and activities that strengthen our school community bonds.
              </p>
            </div>

            <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#febc04] transition-all duration-200 shadow-sm hover:shadow-md">
              <div className="w-14 h-14 bg-[#123f1e]/10 rounded-xl flex items-center justify-center mb-4">
                <Calendar className="w-7 h-7 text-[#123f1e]" />
              </div>
              <h3 className="text-xl font-bold text-[#123f1e] mb-3 tracking-tight">Plan & Collaborate</h3>
              <p className="text-gray-600 leading-relaxed">
                Work with staff to plan enrichment programs, cultural celebrations, and educational workshops for families.
              </p>
            </div>
          </div>
        </div>

        {/* Community Collaboration Image */}
        <div className="mb-16">
          <img
            src="/assets/images/gallery/District9PreK-60.jpg"
            alt="Students and teachers collaborating in a GoLD Pre-K classroom"
            className="w-full max-w-4xl mx-auto h-72 object-cover rounded-3xl shadow-xl"
          />
        </div>

        {/* Important Documents - Organized by Category */}
        <div className="mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-8 tracking-tight">Important Documents & Resources</h2>

          {categories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12 last:mb-0">
              <h3 className="text-2xl font-bold text-[#123f1e] mb-6 tracking-tight flex items-center gap-3">
                <span className="w-2 h-8 bg-[#febc04] rounded-full"></span>
                {category}
              </h3>
              <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
                {documents.filter(doc => doc.category === category).map((doc, index) => (
                  <div
                    key={index}
                    className="group relative bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-[#febc04] transition-all duration-200 shadow-sm hover:shadow-lg overflow-hidden"
                  >
                    <div className={`absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r ${doc.color} opacity-0 group-hover:opacity-100 transition-opacity duration-200`}></div>

                    <div className="flex items-start gap-4 mb-4">
                      <div className={`p-4 bg-gradient-to-br ${doc.color} rounded-xl flex-shrink-0 shadow-md`}>
                        <div className="text-white">
                          {doc.icon}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-xl font-bold text-[#123f1e] mb-2 tracking-tight group-hover:text-[#1a5a2d] transition-colors">
                          {doc.title}
                        </h4>
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded uppercase">
                          {doc.type.toUpperCase()}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                      {doc.description}
                    </p>

                    <div className="flex gap-3">
                      <button
                        onClick={() => setSelectedDoc(doc)}
                        className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#123f1e] text-white font-semibold rounded-xl hover:bg-[#1a5a2d] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        <span>View Document</span>
                      </button>
                      <a
                        href={doc.file}
                        download
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#123f1e] text-[#123f1e] font-semibold rounded-xl hover:bg-[#123f1e]/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2"
                        title="Download document"
                      >
                        <Download className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Get Involved */}
        <div className="bg-[#febc04]/10 border-2 border-[#febc04]/30 rounded-2xl p-8 sm:p-12">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-4 tracking-tight">Get Involved</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8 font-light">
              We welcome all parents and guardians to participate in our Parent Advisory Committee.
              Your input and involvement make a real difference in shaping the future of our school.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 bg-[#123f1e] text-white font-semibold rounded-xl hover:bg-[#1a5a2d] transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2"
              >
                Contact Parent Coordinator
              </a>
              <a
                href="/calendar"
                className="inline-flex items-center justify-center gap-2.5 px-8 py-4 border-2 border-[#123f1e] text-[#123f1e] font-semibold rounded-xl hover:bg-[#123f1e]/5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#123f1e] focus:ring-offset-2"
              >
                View Meeting Schedule
              </a>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mt-16 grid md:grid-cols-3 gap-8">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-[#febc04] transition-all duration-200 shadow-sm">
            <h3 className="font-bold text-[#123f1e] mb-2">4179 Third Ave.</h3>
            <p className="text-sm text-gray-600">Bronx, NY 10457</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-[#febc04] transition-all duration-200 shadow-sm">
            <h3 className="font-bold text-[#123f1e] mb-2">1434 Ogden Ave.</h3>
            <p className="text-sm text-gray-600">Bronx, NY 10452</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:border-[#febc04] transition-all duration-200 shadow-sm">
            <h3 className="font-bold text-[#123f1e] mb-2">1302 Edward L. Grant Hwy</h3>
            <p className="text-sm text-gray-600">(Aurelia Greene Campus)</p>
            <p className="text-sm text-gray-600">Bronx, NY 10452</p>
          </div>
        </div>

        {/* Additional Note */}
        <div className="mt-12 bg-[#123f1e] rounded-2xl p-8 text-white text-center shadow-md">
          <p className="text-lg font-light leading-relaxed">
            For questions about the Parent Advisory Committee or to access documents in alternative formats,
            please contact our Parent Coordinator, Ms. Abigail Freeland, at{' '}
            <a href="tel:718-583-8975" className="font-semibold text-[#febc04] hover:underline focus:outline-none focus:underline">
              (718) 583-8975
            </a>
          </p>
        </div>

        {/* KidsTown USA Partnership - Exclusive Offer */}
        <div className="mt-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#123f1e] mb-8 tracking-tight text-center">Exclusive Family Offers</h2>

          <div className="relative bg-[#123f1e] rounded-2xl p-8 sm:p-12 shadow-xl overflow-hidden">
            {/* Decorative elements */}
            <div aria-hidden="true" className="absolute -z-10 top-0 right-0 w-64 h-64 bg-[#febc04] rounded-full blur-3xl opacity-10"></div>
            <div aria-hidden="true" className="absolute -z-10 bottom-0 left-0 w-48 h-48 bg-[#febc04] rounded-full blur-2xl opacity-10"></div>

            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                {/* Left: Icon and Badge */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div aria-hidden="true" className="absolute -z-10 inset-0 bg-[#febc04] rounded-2xl blur-xl opacity-50"></div>
                    <div className="relative p-6 bg-white rounded-2xl shadow-lg">
                      <Tag className="w-16 h-16 text-[#123f1e]" />
                    </div>
                  </div>
                  <div className="mt-4 text-center">
                    <span className="inline-block px-4 py-2 bg-[#febc04] text-[#123f1e] text-xs font-bold rounded-full uppercase tracking-wide shadow-md">
                      Coupon Coming Soon
                    </span>
                  </div>
                </div>

                {/* Right: Content */}
                <div className="flex-1 text-center lg:text-left">
                  <h3 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
                    KidsTown USA Partnership
                  </h3>
                  <p className="text-lg text-white/90 leading-relaxed font-light mb-6">
                    We're excited to partner with KidsTown USA to bring exclusive savings to our GoLD families!
                    Check back soon for special coupons and offers on children's clothing, toys, and more.
                  </p>
                  <a
                    href="http://www.kidstownusa.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2.5 px-8 py-4 bg-[#febc04] text-[#123f1e] font-semibold rounded-xl hover:bg-[#ffd04f] transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#febc04] focus:ring-offset-2 focus:ring-offset-[#123f1e]"
                  >
                    <span>Visit KidsTown USA</span>
                    <ExternalLink className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {selectedDoc && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="doc-modal-title">
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className={`p-3 bg-gradient-to-br ${selectedDoc.color} rounded-xl shadow-md`}>
                  <div className="text-white">
                    {selectedDoc.icon}
                  </div>
                </div>
                <div>
                  <h3 id="doc-modal-title" className="text-2xl font-bold text-[#123f1e] tracking-tight">{selectedDoc.title}</h3>
                  <p className="text-sm text-gray-600">{selectedDoc.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedDoc.file}
                  download
                  className="p-3 text-[#123f1e] hover:bg-gray-100 rounded-xl transition-colors duration-200"
                  title="Download document"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button
                  onClick={() => setSelectedDoc(null)}
                  className="p-3 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                  aria-label="Close viewer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 p-6 overflow-hidden min-h-0">
              <iframe
                src={selectedDoc.file}
                className="w-full h-full rounded-lg border border-gray-200"
                title={selectedDoc.title}
              />
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default ParentAdvisory;
